import React, { forwardRef } from 'react';

import classNames from 'classnames';

import { InfiniteScroll } from '../../InfiniteScroll';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';

import useStyles from './ListBox.styles';

/**
 * Компонент ListBox представляет собой контейнер для списка элементов с поддержкой бесконечной прокрутки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {boolean} props.hasMore - Флаг, указывающий, есть ли еще данные для загрузки.
 * @param {number} props.dataLength - Текущая длина данных в списке.
 * @param {() => void} props.next - Функция, которая будет вызвана для загрузки следующей порции данных.
 * @param {boolean} props.loading - Флаг, указывающий, что данные загружаются.
 * @param {boolean} [props.long=false] - Флаг, указывающий на длинный список.
 * @param {React.ReactNode | React.ReactNode[]} [props.children] - Дочерние элементы, которые будут отображаться в списке.
 * @param {React.Ref<HTMLDivElement>} ref - Реф для доступа к DOM-элементу компонента.
 * @returns {React.ReactNode} Возвращает JSX-элемент с бесконечной прокруткой.
 */
export const ListBox = forwardRef<
  HTMLDivElement,
  {
    hasMore: boolean;
    dataLength: number;
    next: () => void;
    loading: boolean;
    long?: boolean;
    children?: React.ReactNode | React.ReactNode[];
  }
>(
  (
    { hasMore, dataLength, next, loading, long = false, children, ...rest },
    ref,
  ) => {
    const classes = useStyles();

    return (
      <div
        {...rest}
        id="scrollable"
        ref={ref}
        className={classNames(classes.scrollableWrapper, {
          [classes.longScrollableWrapper]: long,
        })}
      >
        <InfiniteScroll
          next={next}
          hasMore={hasMore}
          dataLength={dataLength}
          loader={
            loading && (
              <Box display="flex" justifyContent="center" p="16">
                <CircularProgress size={30} />
              </Box>
            )
          }
          endMessage={<div />}
          scrollableTarget="scrollable"
        >
          <ul className={classes.listBox}>{children}</ul>
        </InfiniteScroll>
      </div>
    );
  },
);
